import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AssignUserModal from '../../cases-list/components/AssignUserModal';
import { toast } from 'sonner';
import LotGroup2WorkflowPanel from './LotGroup2WorkflowPanel';
import { isProductionManagerRole } from '../../../lib/operatorRoles';

// ─── Production Sheet Edit Modal ──────────────────────────────────────────────
const EditProductionSheetModal = ({ batch, onSave, onClose }) => {
  const [form, setForm] = useState({
    // Modèle/plaquage section
    model_up_from: batch?.model_up_from ?? 0,
    model_up_to: batch?.model_up_to ?? 12,
    model_low_from: batch?.model_low_from ?? 0,
    model_low_to: batch?.model_low_to ?? 12,
    elastic: batch?.elastic || '',
    ali_confirmation: batch?.ali_confirmation || 'Desciption...',
    suppression: batch?.suppression || '',
    
    // Découpe/finition section
    label_from: batch?.label_from ?? 0,
    label_to: batch?.label_to ?? 12,
    of_number: batch?.of_number ?? '',
    
    // Packaging section
    packaging_date: batch?.packaging_date?.slice(0, 10) || '',
  });
  
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const errs = {};
    
    // Validate numeric fields
    if (form.model_up_from < 0) errs.model_up_from = 'Doit être >= 0';
    if (form.model_up_to < 0) errs.model_up_to = 'Doit être >= 0';
    if (form.model_low_from < 0) errs.model_low_from = 'Doit être >= 0';
    if (form.model_low_to < 0) errs.model_low_to = 'Doit être >= 0';
    if (form.label_from < 0) errs.label_from = 'Doit être >= 0';
    if (form.label_to < 0) errs.label_to = 'Doit être >= 0';
    
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        model_up_from: Number(form.model_up_from),
        model_up_to: Number(form.model_up_to),
        model_low_from: Number(form.model_low_from),
        model_low_to: Number(form.model_low_to),
        elastic: form.elastic,
        ali_confirmation: form.ali_confirmation,
        suppression: form.suppression,
        label_from: Number(form.label_from),
        label_to: Number(form.label_to),
        of_number: Number(form.model_up_to || 0) + Number(form.model_low_to || 0),
        packaging_date: form.packaging_date || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const NumberField = ({ label, field, min = 0 }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <input
        type="number"
        min={min}
        value={form[field]}
           readOnly
        onChange={set(field)}
        className={`px-3 py-2 text-sm border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
          errors[field] ? 'border-error' : 'border-border'
        }`}
      />
      {errors[field] && <span className="text-xs text-error">{errors[field]}</span>}
    </div>
  );

  const TextField = ({ label, field, multiline = false }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {multiline ? (
        <textarea
          value={form[field]}
          onChange={set(field)}
          readOnly
          rows={3}
          className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
        />
      ) : (
        <input
          type="text"
          value={form[field]}
             readOnly
          onChange={set(field)}
          className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-card rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="FileText" size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Fiche de Production</h2>
              <p className="text-xs text-muted-foreground">
                Lot {batch?.batchNumber} · {batch?.patientName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-muted flex items-center justify-center">
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1 space-y-6">
          {/* Modèle/plaquage Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-primary/20">
              <Icon name="Box" size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Modèle/plaquage
              </h3>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">UP</p>
                <div className="grid grid-cols-2 gap-2">
                  <NumberField label="De" field="model_up_from" />
                  <NumberField label="À" field="model_up_to" />
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">LOW</p>
                <div className="grid grid-cols-2 gap-2">
                  <NumberField label="De" field="model_low_from" />
                  <NumberField label="À" field="model_low_to" />
                </div>
              </div>
            </div>
            
            <TextField label="Élastique" field="elastic" />
            <TextField label="Description" field="ali_confirmation" multiline />
          
          </div>

          {/* Étiquette Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-primary/20">
              <Icon name="Scissors" size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Étiquette
              </h3>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <NumberField label="De" field="label_from" />
              <NumberField label="À" field="label_to" />
            </div>
          </div>

          {/* Packaging Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-primary/20">
              <Icon name="Package" size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Packaging
              </h3>
            </div>
            
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Date de packaging</label>
              <input
                type="date"
                value={form.packaging_date}
                onChange={set('packaging_date')}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
       
        </div>
      </div>
    </div>
  );
};

// ─── Assign Machine Modal ─────────────────────────────────────────────────────
const AssignMachineModal = ({ batch, machines, onAssign, onClose }) => {
  const [selectedMachine, setSelectedMachine] = useState(batch?.machine?.id || '');
  const [saving, setSaving] = useState(false);

  const availableMachines = machines.filter(
    m => m.status === 'available' || m.status === 'operational'
  );

  const handleSave = async () => {
    if (!selectedMachine) return;
    setSaving(true);
    try {
      await onAssign(selectedMachine);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const getStatusDot = (status) => {
    const dots = {
      operational: 'bg-success',
      available:   'bg-primary',
      maintenance: 'bg-warning',
      offline:     'bg-error',
    };
    return dots[status] || 'bg-muted-foreground';
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Settings" size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Assigner une machine</h2>
              <p className="text-xs text-muted-foreground">Lot {batch?.batchNumber}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-muted flex items-center justify-center">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="p-5">
          {availableMachines.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="AlertCircle" size={32} className="mx-auto mb-3 opacity-50" />
              <p className="text-sm">Aucune machine disponible ou opérationnelle</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {availableMachines.map(m => (
                <label
                  key={m.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedMachine === m.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="machine"
                    value={m.id}
                    checked={selectedMachine === m.id}
                    onChange={() => setSelectedMachine(m.id)}
                    className="accent-primary"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${getStatusDot(m.status)}`} />
                      <span className="text-sm font-medium text-foreground">{m.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {m.model} · Utilisation : {m.utilizationRate}%
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            size="sm"
            iconName="Check"
            onClick={handleSave}
            disabled={!selectedMachine || saving}
            loading={saving}
          >
            {saving ? 'Assignation...' : 'Assigner'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Create Production Sheet Modal ────────────────────────────────────────────
const CreateSheetModal = ({ batch, operators, onCreate, onClose }) => {
  const [form, setForm] = useState({
    reference:          `FS-${batch?.batchNumber || ''}`,
    product:            'Gouttières orthodontiques',
    quantity:           '1',
    operator_id:        '',
    operator_name:      '',
    operator_matricule: '',
    operator_position:  '',
    expected_end_date:  batch?.deadline?.slice(0, 10) || '',
    
    // Production sheet fields from Modèle/plaquage
    model_up_from:      batch?.model_up_from ?? 0,
    model_up_to:        batch?.model_up_to ?? 12,
    model_low_from:     batch?.model_low_from ?? 0,
    model_low_to:       batch?.model_low_to ?? 12,
    elastic:            batch?.elastic || '',
    ali_confirmation:   batch?.ali_confirmation || 'Description...',
    suppression:        batch?.suppression || '',
    
    // Production sheet fields from Découpe/finition
    label_from:         batch?.label_from ?? 0,
    label_to:           batch?.label_to ?? 12,
    of_number:          batch?.of_number ?? '',
    
    // Production sheet field from Packaging
    packaging_date:     batch?.packaging_date?.slice(0, 10) || '',
  });
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState({});

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm(prev => {
      const next = { ...prev, [k]: v };
      if (k === 'operator_id' && v) {
        const op = operators.find(o => o.id === v);
        if (op) {
          next.operator_name      = op.name;
          next.operator_matricule = op.id.slice(0, 8).toUpperCase();
          next.operator_position  = op.role;
        }
      }
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    if (!form.reference.trim())          errs.reference          = 'Champ requis';
    if (!form.product.trim())            errs.product            = 'Champ requis';
    if (!form.quantity || Number(form.quantity) < 1) errs.quantity = 'Valeur invalide';
    if (!form.operator_name.trim())      errs.operator_name      = 'Champ requis';
    if (!form.operator_matricule.trim()) errs.operator_matricule = 'Champ requis';
    if (!form.operator_position.trim())  errs.operator_position  = 'Champ requis';
    if (!form.expected_end_date)         errs.expected_end_date  = 'Champ requis';
    
    // Validate production sheet numeric fields
    if (form.model_up_from < 0) errs.model_up_from = 'Doit être >= 0';
    if (form.model_up_to < 0) errs.model_up_to = 'Doit être >= 0';
    if (form.model_low_from < 0) errs.model_low_from = 'Doit être >= 0';
    if (form.model_low_to < 0) errs.model_low_to = 'Doit être >= 0';
    if (form.label_from < 0) errs.label_from = 'Doit être >= 0';
    if (form.label_to < 0) errs.label_to = 'Doit être >= 0';
    
    return errs;
  };

  const handleSave = async () => {
  
    setSaving(true);
    try {
      await onCreate({
        ...form,
        quantity: Number(form.quantity),
        start_date: new Date().toISOString(),
        operator_id: form.operator_id || null,
        // Production sheet fields
        model_up_from: Number(form.model_up_from),
        model_up_to: Number(form.model_up_to),
        model_low_from: Number(form.model_low_from),
        model_low_to: Number(form.model_low_to),
        elastic: form.elastic,
        ali_confirmation: form.ali_confirmation,
        suppression: form.suppression,
        label_from: Number(form.label_from),
        label_to: Number(form.label_to),
        of_number: Number(form.model_up_to || 0) + Number(form.model_low_to || 0),
        packaging_date: form.packaging_date || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const NumberField = ({ label, field, min = 0 }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <input
        type="number"
        min={min}
         readOnly
        value={form[field]}
        onChange={set(field)}
        className={`px-3 py-2 text-sm border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
          errors[field] ? 'border-error' : 'border-border'
        }`}
      />
      {errors[field] && <span className="text-xs text-error">{errors[field]}</span>}
    </div>
  );

  const TextField = ({ label, field, multiline = false }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {multiline ? (
        <textarea
          value={form[field]}
          onChange={set(field)}
          rows={3}
          readOnly
          className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
        />
      ) : (
        <input
          type="text"
           readOnly
          value={form[field]}
          onChange={set(field)}
          className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-card rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center">
              <Icon name="FileText" size={16} className="text-success" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Fiche de production</h2>
              <p className="text-xs text-muted-foreground">
                Lot {batch?.batchNumber} · {batch?.patientName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-muted flex items-center justify-center">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* Modèle/plaquage Section */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Box" size={16} className="text-primary" />
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wide">
                Modèle/plaquage
              </h3>
            </div>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">UP</p>
                  <div className="grid grid-cols-2 gap-2">
                    <NumberField label="De" field="model_up_from" />
                    <NumberField label="À" field="model_up_to" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">LOW</p>
                  <div className="grid grid-cols-2 gap-2">
                    <NumberField label="De" field="model_low_from" />
                    <NumberField label="À" field="model_low_to" />
                  </div>
                </div>
              </div>
              
              <TextField label="Élastique" field="elastic" />
              <TextField label="Description" field="ali_confirmation" multiline />
      
            </div>
          </div>

          {/* Étiquette Section */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Scissors" size={16} className="text-primary" />
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wide">
                Étiquette
              </h3>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <NumberField label="De" field="label_from" />
              <NumberField label="À" field="label_to" />
            </div>
          </div>

          {/* Packaging Section */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Package" size={16} className="text-primary" />
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wide">
                Packaging
              </h3>
            </div>
            
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Date de packaging</label>
              <input
                type="date"
                value={form.packaging_date}
                onChange={set('packaging_date')}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
        
        </div>
      </div>
    </div>
  );
};

// ─── Edit Batch Modal ─────────────────────────────────────────────────────────
const EditBatchModal = ({ batch, onSave, onClose }) => {
  const [status,   setStatus]   = useState(batch?.status   || 'pending');
  const [progress, setProgress] = useState(batch?.progress ?? 0);
  const [saving,   setSaving]   = useState(false);

  const statuses = [
    { value: 'pending',       label: 'En attente',        icon: 'Clock',        color: 'text-muted-foreground' },
    { value: 'in_progress',   label: 'En cours',          icon: 'Play',         color: 'text-primary'          },
    { value: 'quality_check', label: 'Contrôle qualité',  icon: 'CheckCircle',  color: 'text-warning'          },
    { value: 'packaging',     label: 'Emballage',         icon: 'Package',      color: 'text-success'          },
    { value: 'completed',     label: 'Terminé',           icon: 'CheckCircle2', color: 'text-success'          },
    { value: 'delayed',       label: 'Retardé',           icon: 'AlertCircle',  color: 'text-error'            },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ status, progress: Number(progress) });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center">
              <Icon name="Edit2" size={16} className="text-warning" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Modifier le lot</h2>
              <p className="text-xs text-muted-foreground">
                Lot {batch?.batchNumber} · {batch?.patientName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-muted flex items-center justify-center">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Status selector */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-3">
              Statut
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statuses.map(s => (
                <button
                  key={s.value}
                  onClick={() => setStatus(s.value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    status === s.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:bg-muted/40 text-muted-foreground'
                  }`}
                >
                  <Icon name={s.icon} size={14} className={status === s.value ? 'text-primary' : s.color} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Progress slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Progression
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={e => setProgress(Math.min(100, Math.max(0, Number(e.target.value))))}
                  className="w-16 px-2 py-1 text-sm text-center border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm font-semibold text-foreground">%</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={e => setProgress(Number(e.target.value))}
              className="w-full h-2 rounded-full accent-primary cursor-pointer"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
            {/* Visual progress bar preview */}
            <div className="mt-3 w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary rounded-full h-2 transition-all duration-200"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button size="sm" iconName="Save" onClick={handleSave} disabled={saving} loading={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Card ────────────────────────────────────────────────────────────────
const ProductionBatchCard = ({
  batch,
  machines   = [],
  operators  = [],
  userRole,
  userRoleTypes = [],
  onUpdate,
  onDelete,
  onStatusChange,
  onAssignMachine,
  onCreateSheet,
  onLaunchProduction,
  onAssignResponsible,
  onGenerateInvoice,
  canOpenWorkshop = false,
}) => {
  const navigate = useNavigate();

  const [showAssignModal,       setShowAssignModal]       = useState(false);
  const [showSheetModal,        setShowSheetModal]        = useState(false);
  const [showEditModal,         setShowEditModal]         = useState(false);
  const [showProductionSheet,   setShowProductionSheet]   = useState(false);
  const [showResponsibleModal,  setShowResponsibleModal]  = useState(false);
  const [launching,             setLaunching]             = useState(false);

  const getStatusConfig = (status) => {
    const configs = {
      in_progress:   { label: 'En cours',         className: 'bg-primary/10 text-primary',     icon: 'Play'         },
      quality_check: { label: 'Contrôle qualité', className: 'bg-warning/10 text-warning',     icon: 'CheckCircle'  },
      packaging:     { label: 'Emballage',        className: 'bg-success/10 text-success',     icon: 'Package'      },
      pending:       { label: 'En attente',       className: 'bg-muted text-muted-foreground', icon: 'Clock'        },
      delayed:       { label: 'Retardé',          className: 'bg-error/10 text-error',         icon: 'AlertCircle'  },
      completed:     { label: 'Terminé',          className: 'bg-success/10 text-success',     icon: 'CheckCircle2' },
    };
    return configs?.[status] || configs?.pending;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      urgent: { label: 'Urgente', className: 'bg-error/10 text-error',     icon: 'AlertTriangle' },
      high:   { label: 'Haute',   className: 'bg-error/10 text-error',     icon: 'AlertTriangle' },
      medium: { label: 'Moyenne', className: 'bg-warning/10 text-warning', icon: 'AlertCircle'   },
      low:    { label: 'Basse',   className: 'bg-success/10 text-success', icon: 'Info'          },
    };
    return configs?.[priority] || configs?.medium;
  };

  const statusConfig   = getStatusConfig(batch?.status);
  const priorityConfig = getPriorityConfig(batch?.priority);
  const isDraft = batch?.status === 'draft';

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const isUrgent = () => {
    if (!batch?.deadline) return false;
    return Math.ceil((new Date(batch.deadline) - new Date()) / (1000 * 60 * 60 * 24)) <= 2;
  };

  const machineName = batch?.machine?.machine_name || batch?.machine?.machine_code || '—';
  const showActionButtons = (batch?.currentPhase === 'pending' && userRole ==='administrator');

const handleLaunch = async () => {
  toast(`Lancer la production pour le lot ${batch?.batchNumber} ? Cela va également générer la facture pour ce lot.`, {
    action: {
      label: 'Confirmer',
      onClick: async () => {
        setLaunching(true);
        try {
          await onLaunchProduction();

          if (onGenerateInvoice) {
            try {
              await onGenerateInvoice(batch);
              toast.success('Production lancée');
            } catch (error) {
              console.error('Error generating invoice:', error);
              toast.warning('Production lancée, mais la génération de facture a échoué', {
                description: error.message
              });
            }
          } else {
            toast.success('Production lancée avec succès');
          }
        } catch (error) {
          console.error('Error launching production:', error);
          toast.error('Erreur lors du lancement de la production', {
            description: error.message
          });
        } finally {
          setLaunching(false);
        }
      },
    },
    cancel: {
      label: 'Annuler',
      onClick: () => {},
    },
    duration: 10000,
  });
};

  const handleEditSave = async ({ status, progress }) => {
    await onUpdate({ status, progress });
  };

  const handleProductionSheetSave = async (sheetData) => {
    await onUpdate(sheetData);
  };

  const handleAssignResponsible = async (userId) => {
    await onAssignResponsible(batch.id, userId);
    setShowResponsibleModal(false);
  };

  return (
    <>
<div className={`relative bg-card border border-border rounded-xl p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow duration-200 w-full min-w-0 ${isDraft ? 'opacity-40 pointer-events-none select-none' : ''}`}>
         {isDraft && (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-background/30 pointer-events-none">
      <div className="flex flex-col items-center gap-2 px-4 py-3 bg-card border border-border rounded-lg shadow-md pointer-events-none">
        <Icon name="Clock" size={20} className="text-muted-foreground" />
        <p className="text-sm font-medium text-muted-foreground text-center">
          Pas encore disponible
        </p>
        <p className="text-xs text-muted-foreground text-center">
          Ce lot est en cours de préparation et sera visible une fois prêt à être lancé.
        </p>
      </div>
    </div>
  )}
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              Lot de production
            </p>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-lg font-bold text-foreground tabular-nums">{batch?.batchNumber}</h3>
              {isUrgent() && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-error/10 text-error">
                  <Icon name="AlertTriangle" size={12} />
                  Urgent
                </span>
              )}
            </div>
            {(batch?.caseNumber || batch?.caseId) && (
              <p className="text-xs text-primary font-medium mb-2">
                Conception {batch?.caseNumber || batch?.caseId || '—'}
              </p>
            )}
            <p className="text-sm font-medium text-foreground">{batch?.patientName || '—'}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{batch?.clinicName || '—'}</p>
            <div className="flex items-center justify-between text-sm gap-2 mt-2">
              <span className="text-muted-foreground flex-shrink-0">Dentiste</span>
              <span className="font-medium text-foreground text-right truncate">{batch?.dentistName || '—'}</span>
            </div>
            {batch?.responsibleName && (
              <div className="flex items-center gap-1.5 mt-2">
                <Icon name="User" size={12} className="text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground">
                  Responsable :{' '}
                  <span className="font-medium text-foreground">{batch.responsibleName}</span>
                </span>
              </div>
            )}
          </div>

          {/* Status badge + Edit button */}
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusConfig?.className}`}>
              <Icon name={statusConfig?.icon} size={14} />
              {statusConfig?.label}
            </span>
         {batch?.status !== 'pending' && (
    <button
      onClick={() => setShowEditModal(true)}
      title="Modifier statut et progression"
      className="min-touch-target w-11 h-11 md:w-7 md:h-7 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
    >
      <Icon name="Edit2" size={14} />
    </button>
  )}
          </div>

          
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          {(batch?.caseId || batch?.case_id) && (
            <button
              type="button"
              onClick={() => navigate(`/case-detail/${batch.caseId || batch.case_id}`)}
              title="Ouvrir la fiche conception"
              className="min-touch-target flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              <Icon name="ExternalLink" size={14} />
              Fiche conception ({batch.caseNumber || '—'})
            </button>
          )}
          {canOpenWorkshop && batch?.id && (
            <button
              type="button"
              onClick={() => navigate(`/atelier-lot?batch=${encodeURIComponent(batch.id)}`)}
              title="Ouvrir l'atelier sur la phase en cours"
              className="min-touch-target flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-accent/15 text-accent-foreground hover:bg-accent/25 transition-colors border border-border"
            >
              <Icon name="Wrench" size={14} />
              Ouvrir l&apos;atelier
            </button>
          )}
        </div>

        {/* Details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Machine</span>
            <span className="font-medium text-foreground">{machineName}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Début</span>
            <span className="font-medium text-foreground">{formatDate(batch?.startDate)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Livraison</span>
            <span className={`font-medium ${isUrgent() ? 'text-error' : 'text-foreground'}`}>
              {formatDate(batch?.deadline)}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progression</span>
            <span className="font-semibold text-foreground">{batch?.progress ?? 0}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary rounded-full h-2 transition-all duration-300"
              style={{ width: `${batch?.progress ?? 0}%` }}
            />
          </div>
        </div>

        {/* Priority */}
        <div className="flex items-center gap-2 mb-4">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${priorityConfig?.className}`}>
            <Icon name={priorityConfig?.icon} size={12} />
            Priorité {priorityConfig?.label}
          </span>
        </div>

        {/* Notes */}
        {batch?.notes && (
          <div className="mb-4 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground italic">{batch?.notes}</p>
          </div>
        )}

        {/* Action buttons for pending batches */}
        {showActionButtons && userRole === 'administrator' && (
          <div className="pt-4 border-t border-border space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Actions requises
            </p>
            <Button variant="outline" size="lg" iconName="UserPlus" fullWidth onClick={() => setShowResponsibleModal(true)} className="min-touch-target">
              Assigner responsable
            </Button>
            <Button variant="outline" size="lg" iconName="Settings" fullWidth onClick={() => setShowAssignModal(true)} className="min-touch-target">
              Assigner une machine
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="FileText"
              fullWidth
              onClick={() => {
                const caseId = batch?.caseId || batch?.case_id;
                const batchId = batch?.id;
                const params = new URLSearchParams();
                if (caseId) params.set('caseId', String(caseId));
                if (batchId) params.set('batchId', String(batchId));
                window.open(`/production-sheet?${params.toString()}`, '_blank');
              }}
              className="min-touch-target"
            >
              Fiche de production
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="FileHeart"
              fullWidth
              disabled={!(batch?.caseId || batch?.case_id)}
              onClick={() => {
                const caseId = batch?.caseId || batch?.case_id;
                if (!caseId) return;
                const params = new URLSearchParams({ caseId: String(caseId) });
                window.open(`/ipr-stripping-sheet?${params.toString()}`, '_blank');
              }}
              className="min-touch-target"
            >
              Voir fiche IPR
            </Button>
          </div>
        )}
{(userRole === 'administrator' || isProductionManagerRole(userRole)) && (
  batch?.status === 'pending' ? (
      <Button
      size="lg"
      iconName="Play"
      fullWidth
      onClick={handleLaunch}
      loading={launching}
      disabled={launching}
      className="min-touch-target"
    >
      {launching ? 'Lancement...' : 'Lancer la production'}
    </Button>
    
  ) : (
  <Button
      variant="outline"
      size="lg"
      iconName="Edit2"
      fullWidth
      onClick={() => setShowEditModal(true)}
      className="min-touch-target"
    >
      Modifier progression
    </Button>
  )
)}
        
        {/* Additional actions for all batches */}
        {!showActionButtons && (
          <div className="pt-4 border-t border-border space-y-2">
            <Button variant="outline" size="lg" iconName="UserPlus" fullWidth onClick={() => setShowResponsibleModal(true)} className="min-touch-target">
              {batch?.responsibleName ? 'Changer responsable' : 'Assigner responsable'}
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="FileText"
              fullWidth
              onClick={() => {
                const caseId = batch?.caseId || batch?.case_id;
                const batchId = batch?.id;
                const params = new URLSearchParams();
                if (caseId) params.set('caseId', String(caseId));
                if (batchId) params.set('batchId', String(batchId));
                window.open(`/production-sheet?${params.toString()}`, '_blank');
              }}
              className="min-touch-target"
            >
              Voir fiche de production
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="FileHeart"
              fullWidth
              disabled={!(batch?.caseId || batch?.case_id)}
              onClick={() => {
                const caseId = batch?.caseId || batch?.case_id;
                if (!caseId) return;
                const params = new URLSearchParams({ caseId: String(caseId) });
                window.open(`/ipr-stripping-sheet?${params.toString()}`, '_blank');
              }}
              className="min-touch-target"
            >
              Voir fiche IPR
            </Button>
          </div>
        )}

        <LotGroup2WorkflowPanel
          caseId={batch?.caseId || batch?.case_id}
          caseNumber={batch?.caseNumber}
          currentPhase={batch?.group}
          userRole={userRole}
          userRoleTypes={userRoleTypes}
        />
      </div>

      {/* Modals */}
      {showAssignModal && (
        <AssignMachineModal
          batch={batch}
          machines={machines}
          onAssign={onAssignMachine}
          onClose={() => setShowAssignModal(false)}
        />
      )}
      {showSheetModal && (
        <CreateSheetModal
          batch={batch}
          operators={operators}
          onCreate={onCreateSheet}
          onClose={() => setShowSheetModal(false)}
        />
      )}
      {showEditModal && (
        <EditBatchModal
          batch={batch}
          onSave={handleEditSave}
          onClose={() => setShowEditModal(false)}
        />
      )}
      {showProductionSheet && (
        <EditProductionSheetModal
          batch={batch}
          onSave={handleProductionSheetSave}
          onClose={() => setShowProductionSheet(false)}
        />
      )}
      {showResponsibleModal && (
        <AssignUserModal
          isOpen={showResponsibleModal}
          onClose={() => setShowResponsibleModal(false)}
          onAssign={handleAssignResponsible}
          roleType="production_responsible"
          title="Assigner un responsable de production"
          description="Sélectionnez le responsable de production pour ce lot."
        />
      )}
    </>
  );
};

export default ProductionBatchCard;